import React, { useState, useEffect, useRef, useMemo } from "react";
import DraggablePanel from "./DraggablePanel";
import SceneGraphDiagram from "../SceneGraphDiagram";
import InfoTooltip from "../ui/InfoTooltip";
import panelConfig from "../../utils/panelConfig";
import { PROPERTY_PANEL_CONFIG } from "../../utils/panelConfig";
import { useBodies, useWorldBodies } from "../../stores/sceneStore";
import { getEffectiveWorldBodies } from "../../stores/sceneActions/bodyUtils";
import { useIsMobile } from "../../hooks/useIsMobile";

const { getStandardPanelProps, getStandardPosition } = panelConfig;

const DEFAULT_SCENE_GRAPH_SIZE = {
  width: PROPERTY_PANEL_CONFIG.dimensions.width,
  height: PROPERTY_PANEL_CONFIG.dimensions.height,
};
const SCENE_GRAPH_HELP_TEXT =
  "View and edit the scene structure.\n\nScroll to zoom. Drag or Shift/Alt + scroll to pan. Shift/Alt + drag to pan faster. Ctrl/Cmd + click to select multiples. Right-click for actions.";

const getSceneGraphDefaultLayout = (rootCount = 0) => {
  if (typeof window === "undefined") {
    return {
      position: getStandardPosition(),
      size: DEFAULT_SCENE_GRAPH_SIZE,
    };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const viewportMargin = 10;
  const leftBoundary = 360;

  // Keep clear space for the right-side property panel.
  const propertiesWidth = PROPERTY_PANEL_CONFIG.dimensions.width;
  const propertiesMargin = 20;
  const rightBoundary = viewportWidth - propertiesWidth - propertiesMargin - viewportMargin * 2;
  const availableWidth = Math.max(560, rightBoundary - leftBoundary);
  let width = availableWidth;
  let height = Math.max(500, Math.min(700, viewportHeight - 40));
  if (rootCount <= 1) {
    // Single-root scenes should feel compact and avoid excess empty space.
    width = Math.min(Math.max(620, Math.round(availableWidth * 0.62)), 780);
    height = Math.max(460, Math.min(620, viewportHeight - 60));
  } else if (rootCount === 2) {
    // Two-root scenes: wider than single-root, but still tighter than max.
    width = Math.min(Math.max(720, Math.round(availableWidth * 0.74)), 940);
    height = Math.max(500, Math.min(680, viewportHeight - 50));
  }

  return {
    position: getStandardPosition({ x: leftBoundary, y: 10 }),
    size: {
      width,
      height,
    },
  };
};

const SceneGraphPanel = ({
  open,
  onClose,
  position,
  titleIcon,
  mobilePinPanelId,
  onOpenAssetLibrary,
}) => {
  const isMobile = useIsMobile();
  const [diagramSize, setDiagramSize] = useState({ width: 650, height: 500 });
  const containerRef = useRef(null);
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const effectiveWorldBodies = useMemo(
    () => getEffectiveWorldBodies({ bodies, worldBodies }),
    [bodies, worldBodies]
  );
  const rootCount = Array.isArray(effectiveWorldBodies) ? effectiveWorldBodies.length : 0;
  const layoutMode = rootCount >= 3 ? "root3plus" : rootCount === 2 ? "root2" : "root1";
  const defaultLayout = useMemo(() => getSceneGraphDefaultLayout(rootCount), [rootCount]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDiagramSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [open]);

  return (
    <DraggablePanel
      key={`scene-graph-panel-${layoutMode}`}
      open={open}
      onClose={onClose}
      title="Scene Graph"
      titleIcon={titleIcon}
      titleSuffix={
        <span data-no-panel-drag="true" className="inline-flex items-center">
          <InfoTooltip text={SCENE_GRAPH_HELP_TEXT} widthClass="w-64" />
        </span>
      }
      {...getStandardPanelProps()}
      initialPosition={position ? getStandardPosition(position) : defaultLayout.position}
      initialSize={defaultLayout.size}
      minWidth={450}
      minHeight={350}
      mobilePinPanelId={mobilePinPanelId}
      rootExtraClassName={isMobile ? "draggable-panel--mobile-fullscreen" : ""}
    >
      <div ref={containerRef} className="flex-1 overflow-hidden w-full h-full">
        {open ? (
          <SceneGraphDiagram
            width={diagramSize.width}
            height={diagramSize.height}
            onOpenAssetLibrary={onOpenAssetLibrary}
          />
        ) : null}
      </div>
    </DraggablePanel>
  );
};

export default SceneGraphPanel;
